import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

const API = 'https://api.medlynk.me/api';

function App() {
  const [token, setToken] = useState(localStorage.getItem('ph_token') || null);
  const [pharmacy, setPharmacy] = useState(null);
  const [stock, setStock] = useState([]);
  const [stats, setStats] = useState({ total: 0, inStock: 0, lowStock: 0, stale: 0 });
  const [page, setPage] = useState('dashboard');
  const [phone, setPhone] = useState('+237');
  const [otp, setOtp] = useState('');
  const [authStep, setAuthStep] = useState('phone');
  const [loading, setLoading] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editQty, setEditQty] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [lang, setLang] = useState('fr');

  useEffect(() => {
    if (token) loadPharmacyData();
  }, [token]);

  const requestOTP = async () => {
    if (phone.length < 10) return;
    try {
      setLoading(true);
      const res = await axios.post(`${API}/users/request-otp`, { phone_number: phone, language: 'fr' });
      if (res.data.success) {
        setAuthStep('otp');
        if (res.data.dev_otp) setOtp(res.data.dev_otp);
      }
    } catch (err) { alert('Erreur: ' + err.message); }
    finally { setLoading(false); }
  };

  const verifyOTP = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/users/verify-otp`, { phone_number: phone, otp_code: otp });
      if (res.data.success) {
        localStorage.setItem('ph_token', res.data.token);
        setToken(res.data.token);
      }
    } catch (err) { alert('Code invalide'); }
    finally { setLoading(false); }
  };

  const logout = () => {
    localStorage.removeItem('ph_token');
    setToken(null);
    setPharmacy(null);
    setAuthStep('phone');
    setPhone('+237');
    setOtp('');
  };

  const loadPharmacyData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const dashRes = await axios.get(`${API}/dashboard`, { headers });
      const orgId = dashRes.data.tenant?.organization_id;

      if (orgId) {
        const phRes = await axios.get(`${API}/pharmacies/${orgId}`, { headers });
        setPharmacy(phRes.data.pharmacy || phRes.data);
        const stockData = phRes.data.stock || [];
        setStock(stockData);

        const now = new Date();
        const total = stockData.length;
        const inStock = stockData.filter(s => s.quantity > 0).length;
        const lowStock = stockData.filter(s => s.quantity > 0 && s.quantity < 20).length;
        const stale = stockData.filter(s => {
          const hrs = (now - new Date(s.updated_at)) / 3600000;
          return hrs > 72;
        }).length;
        setStats({ total, inStock, lowStock, stale });
      } else {
        const phRes = await axios.get(`${API}/pharmacies`);
        if (phRes.data.pharmacies?.length > 0) {
          const ph = phRes.data.pharmacies[0];
          setPharmacy(ph);
          const detailRes = await axios.get(`${API}/pharmacies/${ph.id}`);
          const stockData = detailRes.data.stock || [];
          setStock(stockData);
          setStats({
            total: stockData.length,
            inStock: stockData.filter(s => s.quantity > 0).length,
            lowStock: stockData.filter(s => s.quantity > 0 && s.quantity < 20).length,
            stale: 0
          });
        }
      }
    } catch (err) { console.log('Load error:', err.message); }
    finally { setLoading(false); }
  };

  const updateStock = async (pharmacyId, medicineId) => {
    try {
      const qty = parseInt(editQty);
      const price = parseInt(editPrice);
      if (isNaN(qty) || isNaN(price)) { alert('Quantité et prix doivent être des nombres'); return; }
      console.log('Updating:', { pharmacyId, medicineId, qty, price });
      const res = await axios.put(`${API}/pharmacies/${pharmacyId}/stock`, {
        medicine_id: medicineId,
        quantity: qty,
        price_fcfa: price,
      });
      console.log('Update result:', res.data);
      setEditItem(null);
      loadPharmacyData();
    } catch (err) {
      console.log('Update error:', err.response?.data || err.message);
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    }
  };

  const confirmStock = async (pharmacyId, item) => {
    try {
      const mid = item.medicine_id || item.id;
      console.log('Confirming:', { pharmacyId, mid, qty: item.quantity, price: item.price_fcfa });
      await axios.put(`${API}/pharmacies/${pharmacyId}/stock`, {
        medicine_id: mid,
        quantity: item.quantity,
        price_fcfa: item.price_fcfa,
      });
      loadPharmacyData();
    } catch (err) {
      console.log('Confirm error:', err.response?.data || err.message);
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    }
  };

  const getFreshness = (updatedAt) => {
    const hrs = (new Date() - new Date(updatedAt)) / 3600000;
    if (hrs <= 6) return { label: 'Récent', color: '#059669', bg: '#D1FAE5' };
    if (hrs <= 24) return { label: 'Aujourd\'hui', color: '#0D9488', bg: '#CCFBF1' };
    if (hrs <= 72) return { label: 'Vieillissant', color: '#D97706', bg: '#FEF3C7' };
    return { label: 'Obsolète', color: '#DC2626', bg: '#FEE2E2' };
  };

  // LOGIN SCREEN
  if (!token) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-logo">
            <span className="logo-icon">💊</span>
            <h1>Med<span>Lynk</span></h1>
            <p className="login-sub">Espace Pharmacie</p>
          </div>

          {authStep === 'phone' && (
            <>
              <label>Numéro de téléphone</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+237 6XX XXX XXX" />
              <button className="btn-primary" onClick={requestOTP} disabled={loading}>
                {loading ? 'Envoi...' : 'Envoyer le code'}
              </button>
            </>
          )}

          {authStep === 'otp' && (
            <>
              <p className="otp-hint">Code envoyé à {phone}</p>
              <input type="text" value={otp} onChange={e => setOtp(e.target.value)} placeholder="XXXXXX" maxLength={6} />
              <button className="btn-primary" onClick={verifyOTP} disabled={loading}>
                {loading ? 'Vérification...' : 'Vérifier'}
              </button>
              <button className="btn-link" onClick={() => setAuthStep('phone')}>← Changer le numéro</button>
            </>
          )}
        </div>
      </div>
    );
  }

  // DASHBOARD
  return (
    <div className="app">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="logo-icon-sm">💊</span>
          <div>
            <h2>MedLynk</h2>
            <p>Pharmacy Workspace</p>
          </div>
        </div>

        <nav className="sidebar-nav">
          <button className={page === 'dashboard' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('dashboard')}>
            📊 Dashboard
          </button>
          <button className={page === 'stock' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('stock')}>
            💊 Availability
          </button>
          <button className={page === 'profile' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('profile')}>
            🏥 Facility Profile
          </button>
          <button className={page === 'reports' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('reports')}>
            📋 Reports
          </button>
          <button className={page === 'settings' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('settings')}>
            ⚙️ Settings
          </button>
        </nav>

        {pharmacy && (
          <div className="sidebar-pharmacy">
            <strong>{pharmacy.name_fr}</strong>
            <span>{pharmacy.quarter}, {pharmacy.city}</span>
            {pharmacy.listing_status === 'verified' && <span className="verified-badge">✓ Vérifié</span>}
          </div>
        )}

        <div style={{display:'flex',gap:'6px',justifyContent:'center',marginBottom:'12px'}}>
          <button onClick={() => setLang('fr')} style={{padding:'5px 14px',borderRadius:'20px',border: lang==='fr' ? '2px solid #0A2A24' : '1px solid #E5E7EB',background: lang==='fr' ? '#0A2A24' : 'transparent',color: lang==='fr' ? '#fff' : '#6B7280',cursor:'pointer',fontSize:'12px',fontWeight:'600'}}>FR</button>
          <button onClick={() => setLang('en')} style={{padding:'5px 14px',borderRadius:'20px',border: lang==='en' ? '2px solid #0A2A24' : '1px solid #E5E7EB',background: lang==='en' ? '#0A2A24' : 'transparent',color: lang==='en' ? '#fff' : '#6B7280',cursor:'pointer',fontSize:'12px',fontWeight:'600'}}>EN</button>
        </div>
        <button className="btn-logout" onClick={logout}>{lang === 'fr' ? 'Se déconnecter' : 'Sign out'}</button>
      </aside>

      <main className="main-content">
        <header className="top-bar">
          <h1>{page === 'dashboard' ? 'Dashboard' : page === 'stock' ? (lang === 'fr' ? 'Disponibilité des médicaments' : 'Medicine Availability') : page === 'profile' ? (lang === 'fr' ? 'Profil de la pharmacie' : 'Pharmacy Profile') : page === 'reports' ? (lang === 'fr' ? 'Rapports' : 'Reports') : (lang === 'fr' ? 'Paramètres' : 'Settings')}</h1>
          <p>{pharmacy?.name_fr || 'Pharmacie'}</p>
        </header>

        {loading && <div className="loading">Chargement...</div>}

        {/* DASHBOARD HOME */}
        {page === 'dashboard' && !loading && (
          <div className="dashboard">
            <div className="stats-grid">
              <div className="stat-card">
                <p className="stat-label">{lang === 'fr' ? 'Médicaments suivis' : 'Tracked medicines'}</p>
                <h2 className="stat-value">{stats.total}</h2>
              </div>
              <div className="stat-card green">
                <p className="stat-label">{lang === 'fr' ? 'En stock' : 'In stock'}</p>
                <h2 className="stat-value">{stats.inStock}</h2>
                <span className="stat-sub">{stats.total > 0 ? Math.round(stats.inStock / stats.total * 100) : 0}%</span>
              </div>
              <div className="stat-card amber">
                <p className="stat-label">{lang === 'fr' ? 'Stock faible' : 'Low stock'}</p>
                <h2 className="stat-value">{stats.lowStock}</h2>
              </div>
              <div className="stat-card red">
                <p className="stat-label">{lang === 'fr' ? 'Données obsolètes' : 'Stale data'}</p>
                <h2 className="stat-value">{stats.stale}</h2>
              </div>
            </div>

            <div className="dashboard-section">
              <h3>Disponibilité à confirmer</h3>
              <p className="section-sub">Mettez à jour les éléments avec des données vieillissantes</p>
              <div className="stock-list">
                {stock.filter(s => {
                  const hrs = (new Date() - new Date(s.updated_at)) / 3600000;
                  return hrs > 24;
                }).slice(0, 5).map((item, i) => {
                  const fresh = getFreshness(item.updated_at);
                  return (
                    <div key={i} className="stock-row">
                      <div className="stock-info">
                        <strong>{item.name_fr}</strong>
                        <span>{item.dosage_form} — {item.strength}</span>
                      </div>
                      <div className="stock-qty">{item.quantity} unités</div>
                      <span className="freshness-badge" style={{ background: fresh.bg, color: fresh.color }}>{fresh.label}</span>
                      <button className="btn-confirm" onClick={() => confirmStock(pharmacy.id, item)}>Confirmer</button>
                    </div>
                  );
                })}
                {stock.filter(s => (new Date() - new Date(s.updated_at)) / 3600000 > 24).length === 0 && (
                  <p className="empty-msg">✅ Toutes les données sont à jour</p>
                )}
              </div>
            </div>

            <div className="dashboard-section">
              <h3>Fraîcheur des données</h3>
              <div className="freshness-bars">
                <div className="freshness-row">
                  <span>Récent (≤6h)</span>
                  <div className="bar"><div className="bar-fill green" style={{ width: `${stats.total > 0 ? stock.filter(s => (new Date() - new Date(s.updated_at)) / 3600000 <= 6).length / stats.total * 100 : 0}%` }}></div></div>
                  <span>{stock.filter(s => (new Date() - new Date(s.updated_at)) / 3600000 <= 6).length}</span>
                </div>
                <div className="freshness-row">
                  <span>Aujourd'hui (≤24h)</span>
                  <div className="bar"><div className="bar-fill teal" style={{ width: `${stats.total > 0 ? stock.filter(s => { const h = (new Date() - new Date(s.updated_at)) / 3600000; return h > 6 && h <= 24; }).length / stats.total * 100 : 0}%` }}></div></div>
                  <span>{stock.filter(s => { const h = (new Date() - new Date(s.updated_at)) / 3600000; return h > 6 && h <= 24; }).length}</span>
                </div>
                <div className="freshness-row">
                  <span>Vieillissant (≤72h)</span>
                  <div className="bar"><div className="bar-fill amber" style={{ width: `${stats.total > 0 ? stock.filter(s => { const h = (new Date() - new Date(s.updated_at)) / 3600000; return h > 24 && h <= 72; }).length / stats.total * 100 : 0}%` }}></div></div>
                  <span>{stock.filter(s => { const h = (new Date() - new Date(s.updated_at)) / 3600000; return h > 24 && h <= 72; }).length}</span>
                </div>
                <div className="freshness-row">
                  <span>Obsolète (&gt;72h)</span>
                  <div className="bar"><div className="bar-fill red" style={{ width: `${stats.total > 0 ? stock.filter(s => (new Date() - new Date(s.updated_at)) / 3600000 > 72).length / stats.total * 100 : 0}%` }}></div></div>
                  <span>{stock.filter(s => (new Date() - new Date(s.updated_at)) / 3600000 > 72).length}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STOCK MANAGEMENT */}
        {page === 'stock' && !loading && (
          <div className="stock-page">
            <div className="stock-header">
              <h3>{stock.length} médicaments</h3>
              <button className="btn-refresh" onClick={loadPharmacyData}>🔄 Actualiser</button>
            </div>
            <table className="stock-table">
              <thead>
                <tr>
                  <th>Médicament</th>
                  <th>Forme</th>
                  <th>Quantité</th>
                  <th>Prix (FCFA)</th>
                  <th>Fraîcheur</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {stock.map((item, i) => {
                  const fresh = getFreshness(item.updated_at);
                  const isEditing = editItem === item.medicine_id;
                  return (
                    <tr key={i}>
                      <td><strong>{item.name_fr}</strong><br /><span className="text-muted">{item.generic_name}</span></td>
                      <td>{item.dosage_form} — {item.strength}</td>
                      <td>{isEditing ? <input type="number" className="edit-input" value={editQty} onChange={e => setEditQty(e.target.value)} /> : item.quantity}</td>
                      <td>{isEditing ? <input type="number" className="edit-input" value={editPrice} onChange={e => setEditPrice(e.target.value)} /> : item.price_fcfa}</td>
                      <td><span className="freshness-badge" style={{ background: fresh.bg, color: fresh.color }}>{fresh.label}</span></td>
                      <td>
                        {isEditing ? (
                          <>
                            <button className="btn-save" onClick={() => updateStock(pharmacy.id, item.medicine_id)}>💾 Sauver</button>
                            <button className="btn-cancel" onClick={() => setEditItem(null)}>Annuler</button>
                          </>
                        ) : (
                          <>
                            <button className="btn-edit" onClick={() => { setEditItem(item.medicine_id); setEditQty(item.quantity.toString()); setEditPrice(item.price_fcfa.toString()); }}>✏️ Modifier</button>
                            <button className="btn-confirm" onClick={() => confirmStock(pharmacy.id, item)}>✓ Confirmer</button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* FACILITY PROFILE */}
        {page === 'profile' && !loading && pharmacy && (
          <div className="profile-page">
            <div className="profile-card">
              <h3>{pharmacy.name_fr}</h3>
              {pharmacy.name_en && <p className="text-muted">{pharmacy.name_en}</p>}
              <div className="profile-details">
                <div className="detail-row"><span>📍 Adresse</span><strong>{pharmacy.address}</strong></div>
                <div className="detail-row"><span>🏘️ Quartier</span><strong>{pharmacy.quarter}</strong></div>
                <div className="detail-row"><span>🌆 Ville</span><strong>{pharmacy.city}</strong></div>
                <div className="detail-row"><span>📞 Téléphone</span><strong>{pharmacy.phone_number}</strong></div>
                <div className="detail-row"><span>💬 WhatsApp</span><strong>{pharmacy.whatsapp_number || '—'}</strong></div>
                <div className="detail-row"><span>📋 Statut</span><strong className="verified-text">{pharmacy.listing_status === 'verified' ? '✓ Vérifié par MedLynk' : pharmacy.listing_status}</strong></div>
                <div className="detail-row"><span>📍 GPS</span><strong>{pharmacy.latitude}, {pharmacy.longitude}</strong></div>
              </div>
            </div>
          </div>
        )}

        {/* REPORTS */}
        {page === 'reports' && (
          <div className="reports-page">
            <div className="empty-state">
              <span className="empty-icon">📊</span>
              <h3>Rapports bientôt disponibles</h3>
              <p>Les rapports d'activité, de recherches patients et de ventes seront disponibles dans la prochaine mise à jour.</p>
            </div>
          </div>
        )}

        {/* SETTINGS */}
        {page === 'settings' && (
          <div className="settings-page">
            <div className="settings-card">
              <h3>Paramètres</h3>
              <div className="detail-row"><span>🌐 Langue</span><strong>Français</strong></div>
              <div className="detail-row"><span>🔔 Notifications</span><strong>Activées</strong></div>
              <div className="detail-row"><span>📱 Version</span><strong>1.0.0</strong></div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
